import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Eye, Image as ImageIcon, Loader2, MessageSquareText, Pencil, Plus, Sparkles, Trash2, TriangleAlert, Upload, Video } from 'lucide-react';
import {
  getPersona,
  updatePersona,
  getPersonaTemplates,
  applyPersonaTemplate,
  type PersonaPayload,
  type PersonaTemplate,
} from '../../api/persona';
import {
  listSkills,
  createSkill,
  updateSkill,
  deleteSkill,
  type AgentSkill,
  type CreateSkillInput,
} from '../../api/skills';
import {
  listMcpServers,
  createMcpServer,
  updateMcpServer,
  deleteMcpServer,
  type McpServer,
} from '../../api/mcpServers';
import {
  listKnowledge,
  createKnowledge,
  deleteKnowledge,
  uploadKnowledgeDocument,
  type KnowledgeItem,
} from '../../api/knowledge';
import {
  listLlmConfigs,
  createLlmConfig,
  updateAgent,
  getAgent,
  type LlmConfig,
} from '../../api/llmConfigs';
import { bindPlatformModel, fetchModelCatalog } from '../../api/playground';
import { useAgentRuntimeStatus, isRuntimeRunning } from '../../hooks/useAgentRuntimeStatus';
import { toast } from '../../utils/toast';
import Modal from '../ui/Modal';
import type { DailyQuota, ModelCatalogEntry, ModelCatalogGroup, ModelModality, PlaygroundAgent } from '../../types';

/**
 * 工作台右栏 —— 配置面板（五维配置：角色 / 技能 / 工具 / 知识库 / 模型）。
 *
 * Props 契约（后续工作台任务扩展时保持兼容）：
 * - agent: 当前选中的 Agent（未选中时为 null）。
 *
 * 设计要点：
 * - 每个 Tab 顶部一句教学引导文案（这是什么、有什么用）；
 * - 各 Tab 在首次激活时加载数据（切换 Agent 会重载）；
 * - 所有写操作：成功 toast；Agent 运行中追加「下次启动生效」提示；
 * - 模型 Tab 含预算设置（月度成本上限，写入 agent.budget）。
 */

type TabKey = 'persona' | 'skills' | 'mcp' | 'knowledge' | 'llm';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'persona', label: '角色' },
  { key: 'skills', label: '技能' },
  { key: 'mcp', label: '工具' },
  { key: 'knowledge', label: '知识库' },
  { key: 'llm', label: '模型' },
];

const TAB_HINTS: Record<TabKey, string> = {
  persona:
    '人设 = Agent 的「性格说明书」：它是谁、怎么说话、什么不能做。\n为什么重要：决定对话的「感觉」。\n例子：写上「电商客服，语气亲切」，它回话就自带客服腔。',
  skills:
    '技能 = 让 Agent 学会的具体本领：何时用 + 怎么做。\n为什么重要：把高频活儿写成技能，一句话就能触发。\n例子：「写周报」= 每周五 + 按固定模板总结。',
  mcp: '工具（MCP）= 给 Agent 接上的外部能力，比如查数据库、读文件。\n为什么重要：让它不只会说，还能动手。\n例子：接一个 filesystem 工具，它就能帮你整理目录。',
  knowledge:
    '知识库 = 喂给 Agent 的参考资料。\n为什么重要：回答前先翻资料，答案更有依据、更少编造。\n例子：上传一份产品 FAQ，客户问什么都能答上来。',
  llm: '模型 = Agent 的「大脑」，绑定后才能开口说话。\n为什么重要：没绑模型 = 没脑子，什么都聊不了。\n例子：绑定一个 DeepSeek 配置，它就能正常回答你。',
};

/** 写操作成功后的统一提示：Agent 运行中追加「下次启动生效」 */
function useSavedHint(running: boolean) {
  return useCallback(
    (successText = '已保存') => {
      if (running) {
        toast(successText, 'success');
        toast('部分配置将在下次启动后生效（配置热更暂未支持）。', 'info');
      } else {
        toast(successText, 'success');
      }
    },
    [running]
  );
}

function describeError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: unknown };
  if (e.response?.data && typeof e.response.data.detail === 'string') return e.response.data.detail;
  return typeof e.message === 'string' ? e.message : '操作失败，请稍后重试';
}

/* ════════════════════════════════════════════════════════════════
 * 角色 Tab
 * ════════════════════════════════════════════════════════════════ */

function PersonaTab({ agent, runtimeRunning }: { agent: PlaygroundAgent; runtimeRunning: boolean }) {
  const [persona, setPersona] = useState<PersonaPayload | null>(null);
  const [identity, setIdentity] = useState('');
  const [communication, setCommunication] = useState('');
  const [rules, setRules] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<PersonaTemplate[]>([]);
  const [applyId, setApplyId] = useState('');
  const [applying, setApplying] = useState(false);
  const savedHint = useSavedHint(runtimeRunning);

  // 事件处理中复用（套用模板后重载）
  const reload = useCallback(async () => {
    const data = await getPersona(agent.id);
    setPersona(data);
    setIdentity(data.identity?.role ?? '');
    setCommunication(data.communication?.tone ?? '');
    setRules(Array.isArray(data.rules) ? data.rules.join('\n') : data.rules ? String(data.rules) : '');
    return data;
  }, [agent.id]);

  // 挂载时加载（setState 均在 .then/.catch 回调内）
  useEffect(() => {
    let cancelled = false;
    getPersona(agent.id)
      .then((data) => {
        if (cancelled) return;
        setPersona(data);
        setIdentity(data.identity?.role ?? '');
        setCommunication(data.communication?.tone ?? '');
        setRules(Array.isArray(data.rules) ? data.rules.join('\n') : data.rules ? String(data.rules) : '');
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        toast(describeError(err) || '人设加载失败', 'error');
        setLoading(false);
      });
    getPersonaTemplates()
      .then((list) => {
        if (!cancelled) setTemplates(list.slice(0, 8));
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [agent.id]);

  const handleSave = async () => {
    setSaving(true);
    const payload: PersonaPayload = {
      identity: {
        role: identity.trim(),
        domain: persona?.identity?.domain ?? '',
        experience: persona?.identity?.experience ?? '',
      },
      communication: {
        tone: communication.trim(),
        style: persona?.communication?.style ?? '',
        example_phrases: persona?.communication?.example_phrases ?? [],
      },
      rules: rules
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    };
    try {
      await updatePersona(agent.id, payload);
      savedHint('人设已保存');
      setPersona(payload);
    } catch (err) {
      toast(describeError(err) || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!applyId) return;
    setApplying(true);
    try {
      await applyPersonaTemplate(agent.id, applyId);
      await reload();
      savedHint('模板已套用');
      setApplyId('');
    } catch (err) {
      toast(describeError(err) || '套用模板失败', 'error');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="cfg-state">
        <Loader2 size={18} className="spin" />
        加载中…
      </div>
    );
  }

  return (
    <div className="cfg-tab-content">
      <div className="cfg-field">
        <div className="cfg-field__head">
          <label className="form-label" htmlFor="persona-identity">
            身份定位
          </label>
          <button type="button" className="cfg-link" onClick={handleApplyTemplate} disabled={!applyId || applying}>
            {applying ? '套用中…' : '套用模板'}
          </button>
        </div>
        <div className="cfg-field__row">
          <select className="input" value={applyId} onChange={(e) => setApplyId(e.target.value)}>
            <option value="">选择人设模板…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <textarea
          id="persona-identity"
          className="textarea"
          rows={3}
          placeholder="它是什么身份？\n比如：一名耐心友好的电商客服，负责处理退换货咨询"
          value={identity}
          onChange={(e) => setIdentity(e.target.value)}
        />
        <div className="cfg-hint">回答「我是谁、擅长什么」——身份越具体，人设越鲜活。</div>
      </div>

      <div className="cfg-field">
        <label className="form-label" htmlFor="persona-communication">
          语气风格
        </label>
        <textarea
          id="persona-communication"
          className="textarea"
          rows={3}
          placeholder="怎么说话？\n比如：亲切、简洁、多用短句，偶尔用「～」"
          value={communication}
          onChange={(e) => setCommunication(e.target.value)}
        />
        <div className="cfg-hint">回答「怎么说话」——决定了用户第一印象。</div>
      </div>

      <div className="cfg-field">
        <label className="form-label" htmlFor="persona-rules">
          能力边界
        </label>
        <textarea
          id="persona-rules"
          className="textarea"
          rows={4}
          placeholder={'每行一条规则，比如：\n不回答医疗诊断建议\n不编造数据'}
          value={rules}
          onChange={(e) => setRules(e.target.value)}
        />
        <div className="cfg-hint">回答「什么不能做」，每行一条；规则越明确，越不容易跑偏。</div>
      </div>

      <div className="cfg-save-bar">
        <button type="button" className="btn btn-primary" onClick={() => void handleSave()} disabled={saving}>
          {saving && <Loader2 size={14} className="spin" />}
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 * 技能 Tab
 * ════════════════════════════════════════════════════════════════ */

interface SkillFormState {
  name: string;
  description: string;
  whenToUse: string;
  instructions: string;
}

const EMPTY_SKILL_FORM: SkillFormState = { name: '', description: '', whenToUse: '', instructions: '' };

function SkillsTab({ agent, runtimeRunning }: { agent: PlaygroundAgent; runtimeRunning: boolean }) {
  const profileId = agent.active_profile_id;
  const [skills, setSkills] = useState<AgentSkill[] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AgentSkill | null>(null);
  const [form, setForm] = useState<SkillFormState>(EMPTY_SKILL_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<AgentSkill | null>(null);
  const [busyIds, setBusyIds] = useState<Record<string, boolean>>({});
  const savedHint = useSavedHint(runtimeRunning);

  // 事件处理中复用（保存/切换后重载）
  const reload = useCallback(async () => {
    if (!profileId) return;
    setSkills(await listSkills(profileId));
  }, [profileId]);

  // 挂载时加载（setState 均在 .then/.catch 回调内）
  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    listSkills(profileId)
      .then((list) => {
        if (!cancelled) setSkills(list);
      })
      .catch((err) => {
        if (cancelled) return;
        toast(describeError(err) || '技能加载失败', 'error');
        setSkills([]);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (!profileId) {
    return <div className="cfg-state">该 Agent 缺少配置文件（active profile），无法管理技能。</div>;
  }

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_SKILL_FORM);
    setFormOpen(true);
  };

  const openEdit = (skill: AgentSkill) => {
    setEditing(skill);
    setForm({
      name: skill.name ?? '',
      description: skill.description ?? '',
      whenToUse: String(skill.definition?.when_to_use ?? ''),
      instructions: String(skill.definition?.instructions ?? ''),
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      toast('请填写技能名称', 'error');
      return;
    }
    setSaving(true);
    const definition = { when_to_use: form.whenToUse.trim(), instructions: form.instructions.trim() };
    const input: CreateSkillInput = {
      name,
      display_name: name,
      description: form.description.trim() || undefined,
      definition,
    };
    try {
      if (editing) {
        await updateSkill(profileId, editing.id, input);
      } else {
        await createSkill(profileId, input);
      }
      savedHint(editing ? '技能已更新' : '技能已创建');
      setFormOpen(false);
      void reload();
    } catch (err) {
      toast(describeError(err) || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (skill: AgentSkill) => {
    setBusyIds((prev) => ({ ...prev, [skill.id]: true }));
    try {
      const updated = await updateSkill(profileId, skill.id, { is_enabled: !skill.is_enabled });
      setSkills((prev) => (prev ?? []).map((s) => (s.id === skill.id ? { ...s, is_enabled: updated.is_enabled } : s)));
      savedHint(updated.is_enabled ? '技能已启用' : '技能已停用');
    } catch (err) {
      toast(describeError(err) || '切换失败', 'error');
    } finally {
      setBusyIds((prev) => {
        const next = { ...prev };
        delete next[skill.id];
        return next;
      });
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteSkill(profileId, deleting.id);
      setSkills((prev) => (prev ?? []).filter((s) => s.id !== deleting.id));
      toast('技能已删除', 'success');
    } catch (err) {
      toast(describeError(err) || '删除失败', 'error');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="cfg-tab-content">
      <div className="cfg-toolbar">
        <span className="cfg-count">{skills ? `${skills.length} 个技能` : ''}</span>
        <button type="button" className="btn btn-primary cfg-btn-sm" onClick={openCreate}>
          <Plus size={13} />
          新建技能
        </button>
      </div>

      {skills === null ? (
        <div className="cfg-state">
          <Loader2 size={18} className="spin" />
          加载中…
        </div>
      ) : skills.length === 0 ? (
        <div className="cfg-state">还没有技能。点「新建技能」教 Agent 第一个本领吧。</div>
      ) : (
        <div className="cfg-list">
          {skills.map((s) => (
            <div key={s.id} className="cfg-item">
              <div className="cfg-item__main">
                <div className="cfg-item__name">
                  {s.display_name || s.name}
                  <label className="switch" title={s.is_enabled ? '点击停用' : '点击启用'}>
                    <input
                      type="checkbox"
                      checked={!!s.is_enabled}
                      disabled={!!busyIds[s.id]}
                      onChange={() => void handleToggle(s)}
                      aria-label={`启用开关「${s.display_name || s.name}」`}
                    />
                    <span className="switch__track" />
                  </label>
                </div>
                <div className="cfg-item__desc">{s.description || '暂无描述'}</div>
              </div>
              <div className="cfg-item__actions">
                <button type="button" className="btn btn-icon" title="编辑" aria-label={`编辑技能「${s.display_name || s.name}」`} onClick={() => openEdit(s)}>
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  className="btn btn-icon cfg-danger"
                  title="删除"
                  aria-label={`删除技能「${s.display_name || s.name}」`}
                  onClick={() => setDeleting(s)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 新建/编辑对话框 */}
      <Modal
        open={formOpen}
        title={editing ? '编辑技能' : '新建技能'}
        width="md"
        onClose={() => setFormOpen(false)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setFormOpen(false)} disabled={saving}>
              取消
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleSave()} disabled={saving}>
              {saving && <Loader2 size={14} className="spin" />}
              {saving ? '保存中…' : '保存'}
            </button>
          </>
        }
      >
        <div className="form-stack">
          <div className="cfg-field">
            <label className="form-label" htmlFor="skill-name">
              名称 <span style={{ color: 'var(--pg-danger)' }}>*</span>
            </label>
            <input id="skill-name" className="input" placeholder="比如：写周报" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="skill-desc">
              描述
            </label>
            <input id="skill-desc" className="input" placeholder="一句话说明这个技能" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="skill-when">
              何时使用
            </label>
            <textarea id="skill-when" className="textarea" rows={2} placeholder="什么情况下该用这个技能？" value={form.whenToUse} onChange={(e) => setForm({ ...form, whenToUse: e.target.value })} />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="skill-instructions">
              指令正文
            </label>
            <textarea id="skill-instructions" className="textarea" rows={5} placeholder="具体怎么做？写清楚步骤和要点" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          </div>
        </div>
      </Modal>

      {/* 删除确认 */}
      <Modal
        open={!!deleting}
        title="删除技能"
        width="sm"
        onClose={() => setDeleting(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setDeleting(null)}>
              取消
            </button>
            <button type="button" className="btn btn-danger" onClick={() => void handleDelete()}>
              删除
            </button>
          </>
        }
      >
        <div className="confirm-text">
          确定删除技能「{deleting?.display_name || deleting?.name}」吗？
          <br />
          删除后不可恢复。
        </div>
      </Modal>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 * 工具（MCP）Tab
 * ════════════════════════════════════════════════════════════════ */

interface McpFormState {
  name: string;
  command: string;
  argsText: string;
  envText: string;
}

const EMPTY_MCP_FORM: McpFormState = { name: '', command: '', argsText: '', envText: '' };

function McpTab({ agent, runtimeRunning }: { agent: PlaygroundAgent; runtimeRunning: boolean }) {
  const [servers, setServers] = useState<McpServer[] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<McpServer | null>(null);
  const [form, setForm] = useState<McpFormState>(EMPTY_MCP_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<McpServer | null>(null);
  const savedHint = useSavedHint(runtimeRunning);

  // 事件处理中复用（保存后重载）
  const reload = useCallback(async () => {
    setServers(await listMcpServers(agent.id));
  }, [agent.id]);

  // 挂载时加载（setState 均在 .then/.catch 回调内）
  useEffect(() => {
    let cancelled = false;
    listMcpServers(agent.id)
      .then((list) => {
        if (!cancelled) setServers(list);
      })
      .catch((err) => {
        if (cancelled) return;
        toast(describeError(err) || '工具加载失败', 'error');
        setServers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [agent.id]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_MCP_FORM);
    setFormOpen(true);
  };

  const openEdit = (s: McpServer) => {
    setEditing(s);
    setForm({
      name: s.name ?? '',
      command: s.command ?? '',
      argsText: (s.args ?? []).join('\n'),
      envText: Object.entries(s.env ?? {})
        .map(([k, v]) => `${k}=${v}`)
        .join('\n'),
    });
    setFormOpen(true);
  };

  const parseForm = (): { args: string[]; env: Record<string, string> } => {
    const args = form.argsText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const env: Record<string, string> = {};
    for (const line of form.envText.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const idx = trimmed.indexOf('=');
      if (idx <= 0) continue;
      env[trimmed.slice(0, idx).trim()] = trimmed.slice(idx + 1).trim();
    }
    return { args, env };
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name || !form.command.trim()) {
      toast('请填写名称与 command', 'error');
      return;
    }
    const { args, env } = parseForm();
    setSaving(true);
    try {
      if (editing) {
        await updateMcpServer(agent.id, editing.id, { name, command: form.command.trim(), args, env });
      } else {
        await createMcpServer(agent.id, { name, command: form.command.trim(), args, env });
      }
      savedHint(editing ? '工具已更新' : '工具已创建');
      setFormOpen(false);
      void reload();
    } catch (err) {
      toast(describeError(err) || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMcpServer(agent.id, deleting.id);
      setServers((prev) => (prev ?? []).filter((s) => s.id !== deleting.id));
      toast('工具已删除', 'success');
    } catch (err) {
      toast(describeError(err) || '删除失败', 'error');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="cfg-tab-content">
      <div className="cfg-toolbar">
        <span className="cfg-count">{servers ? `${servers.length} 个工具` : ''}</span>
        <button type="button" className="btn btn-primary cfg-btn-sm" onClick={openCreate}>
          <Plus size={13} />
          新建工具
        </button>
      </div>

      {servers === null ? (
        <div className="cfg-state">
          <Loader2 size={18} className="spin" />
          加载中…
        </div>
      ) : servers.length === 0 ? (
        <div className="cfg-state">还没有 MCP 工具。工具适合给 Agent 接上外部数据/操作能力。</div>
      ) : (
        <div className="cfg-list">
          {servers.map((s) => (
            <div key={s.id} className="cfg-item">
              <div className="cfg-item__main">
                <div className="cfg-item__name">{s.name}</div>
                <div className="cfg-item__desc">
                  {s.command}
                  {s.args && s.args.length > 0 ? ` ${s.args.join(' ')}` : ''}
                </div>
              </div>
              <div className="cfg-item__actions">
                <button type="button" className="btn btn-icon" title="编辑" aria-label={`编辑工具「${s.name}」`} onClick={() => openEdit(s)}>
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  className="btn btn-icon cfg-danger"
                  title="删除"
                  aria-label={`删除工具「${s.name}」`}
                  onClick={() => setDeleting(s)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={formOpen}
        title={editing ? '编辑工具' : '新建工具'}
        width="md"
        onClose={() => setFormOpen(false)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setFormOpen(false)} disabled={saving}>
              取消
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleSave()} disabled={saving}>
              {saving && <Loader2 size={14} className="spin" />}
              {saving ? '保存中…' : '保存'}
            </button>
          </>
        }
      >
        <div className="form-stack">
          <div className="cfg-field">
            <label className="form-label" htmlFor="mcp-name">
              名称 <span style={{ color: 'var(--pg-danger)' }}>*</span>
            </label>
            <input id="mcp-name" className="input" placeholder="比如：filesystem" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="mcp-command">
              command <span style={{ color: 'var(--pg-danger)' }}>*</span>
            </label>
            <input id="mcp-command" className="input" placeholder="比如：npx" value={form.command} onChange={(e) => setForm({ ...form, command: e.target.value })} />
            <div className="cfg-hint">启动 MCP 服务的可执行命令。</div>
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="mcp-args">
              args
            </label>
            <textarea id="mcp-args" className="textarea" rows={3} placeholder="每行一个参数" value={form.argsText} onChange={(e) => setForm({ ...form, argsText: e.target.value })} />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="mcp-env">
              env
            </label>
            <textarea id="mcp-env" className="textarea" rows={3} placeholder={'每行一个 KEY=value，如：\nAPI_KEY=xxx'} value={form.envText} onChange={(e) => setForm({ ...form, envText: e.target.value })} />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deleting}
        title="删除工具"
        width="sm"
        onClose={() => setDeleting(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setDeleting(null)}>
              取消
            </button>
            <button type="button" className="btn btn-danger" onClick={() => void handleDelete()}>
              删除
            </button>
          </>
        }
      >
        <div className="confirm-text">
          确定删除工具「{deleting?.name}」吗？
          <br />
          删除后不可恢复。
        </div>
      </Modal>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 * 知识库 Tab
 * ════════════════════════════════════════════════════════════════ */

function KnowledgeTab({ agent }: { agent: PlaygroundAgent }) {
  const [items, setItems] = useState<KnowledgeItem[] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<KnowledgeItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // 事件处理中复用（上传后刷新列表）
  const reload = useCallback(async () => {
    setItems(await listKnowledge(agent.id));
  }, [agent.id]);

  // 挂载时加载（setState 均在 .then/.catch 回调内）
  useEffect(() => {
    let cancelled = false;
    listKnowledge(agent.id)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((err) => {
        if (cancelled) return;
        toast(describeError(err) || '知识库加载失败', 'error');
        setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [agent.id]);

  /** 后端无 title 列：列表展示用内容首行派生标题 */
  const itemTitle = (item: KnowledgeItem): string => {
    const firstLine = (item.content ?? '').split('\n').find((l) => l.trim()) ?? '';
    return firstLine.length > 40 ? `${firstLine.slice(0, 40)}…` : firstLine || '未命名条目';
  };

  const handleCreate = async () => {
    if (!title.trim() && !content.trim()) {
      toast('请填写标题或内容', 'error');
      return;
    }
    setSaving(true);
    try {
      const created = await createKnowledge(agent.id, { title, content });
      setItems((prev) => [created, ...(prev ?? [])]);
      toast('知识条目已添加', 'success');
      setFormOpen(false);
      setTitle('');
      setContent('');
    } catch (err) {
      toast(describeError(err) || '添加失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteKnowledge(agent.id, deleting.id);
      setItems((prev) => (prev ?? []).filter((k) => k.id !== deleting.id));
      toast('知识条目已删除', 'success');
    } catch (err) {
      toast(describeError(err) || '删除失败', 'error');
    } finally {
      setDeleting(null);
    }
  };

  /** 文档上传（docx/pdf/txt/md ≤10MB）：成功后刷新条目列表 */
  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许重复选择同一文件
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadKnowledgeDocument(agent.id, file);
      toast(`「${res.filename}」已解析为 ${res.created} 条知识`, 'success');
      void reload();
    } catch (err) {
      toast(describeError(err) || '上传失败，请稍后重试', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="cfg-tab-content">
      <div className="cfg-toolbar">
        <span className="cfg-count">{items ? `${items.length} 个条目` : ''}</span>
        <div className="cfg-toolbar__actions">
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,.pdf,.txt,.md"
            className="file-hidden"
            aria-label="上传知识文档"
            onChange={(e) => void handleUpload(e)}
          />
          <button
            type="button"
            className="btn cfg-btn-sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            title="上传 docx / pdf / txt / md（≤10MB），自动解析为知识条目"
          >
            {uploading ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
            {uploading ? '解析中…' : '上传文档'}
          </button>
          <button type="button" className="btn btn-primary cfg-btn-sm" onClick={() => setFormOpen(true)}>
            <Plus size={13} />
            新建条目
          </button>
        </div>
      </div>

      {items === null ? (
        <div className="cfg-state">
          <Loader2 size={18} className="spin" />
          加载中…
        </div>
      ) : items.length === 0 ? (
        <div className="cfg-state">知识库还是空的。把常见问题、产品资料喂给它，回答会更有依据。</div>
      ) : (
        <div className="cfg-list">
          {items.map((k) => (
            <div key={k.id} className="cfg-item">
              <div className="cfg-item__main">
                <div className="cfg-item__name">{itemTitle(k)}</div>
                <div className="cfg-item__desc">
                  {(k.content ?? '').split('\n').slice(1).join(' ').slice(0, 60) || '…'}
                </div>
              </div>
              <div className="cfg-item__actions">
                <button
                  type="button"
                  className="btn btn-icon cfg-danger"
                  title="删除"
                  aria-label={`删除知识条目「${itemTitle(k)}」`}
                  onClick={() => setDeleting(k)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={formOpen}
        title="新建知识条目"
        width="md"
        onClose={() => setFormOpen(false)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setFormOpen(false)} disabled={saving}>
              取消
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleCreate()} disabled={saving}>
              {saving && <Loader2 size={14} className="spin" />}
              {saving ? '添加中…' : '添加'}
            </button>
          </>
        }
      >
        <div className="form-stack">
          <div className="cfg-field">
            <label className="form-label" htmlFor="kb-title">
              标题
            </label>
            <input id="kb-title" className="input" placeholder="给这条知识起个标题" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="kb-content">
              内容
            </label>
            <textarea id="kb-content" className="textarea" rows={6} placeholder="把参考资料原文或要点贴进来" value={content} onChange={(e) => setContent(e.target.value)} />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deleting}
        title="删除知识条目"
        width="sm"
        onClose={() => setDeleting(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setDeleting(null)}>
              取消
            </button>
            <button type="button" className="btn btn-danger" onClick={() => void handleDelete()}>
              删除
            </button>
          </>
        }
      >
        <div className="confirm-text">
          确定删除「{deleting ? itemTitle(deleting) : ''}」吗？
          <br />
          删除后不可恢复。
        </div>
      </Modal>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 * 模型 Tab
 * ════════════════════════════════════════════════════════════════ */

const PROVIDER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'claude', label: 'Claude' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'deepseek', label: 'DeepSeek' },
  { value: 'siliconflow', label: '硅基流动' },
  { value: 'moonshot', label: 'Moonshot / Kimi' },
  { value: 'zhipu', label: '智谱 GLM' },
  { value: 'dashscope', label: '通义千问' },
  { value: 'volcengine', label: '火山引擎' },
  { value: 'jdcloud', label: '京东云' },
  { value: 'exgate', label: 'E 网关' },
  { value: 'custom', label: '自定义' },
];

interface LlmFormState {
  name: string;
  provider: string;
  model: string;
  apiKey: string;
  baseUrl: string;
}

const EMPTY_LLM_FORM: LlmFormState = { name: '', provider: 'openai', model: '', apiKey: '', baseUrl: '' };

export interface LlmTabProps {
  agent: PlaygroundAgent;
  runtimeRunning: boolean;
  /** 平台每日额度（bootstrap usage.daily，未启用为 null） */
  daily?: DailyQuota | null;
  /** 模型来源：platform = 当前走平台体验模型 */
  modelSource?: 'own' | 'platform' | null;
  /** 预算保存成功后回调（顶栏进度条即时反映） */
  onBudgetChanged?: (agentId: string, cap: number | null) => void;
}

/** 平台目录分组图标（按模态） */
const MODALITY_ICONS: Record<ModelModality, typeof MessageSquareText> = {
  text: MessageSquareText,
  vision: Eye,
  image: ImageIcon,
  video: Video,
};

function LlmTab({ agent, runtimeRunning, daily, modelSource, onBudgetChanged }: LlmTabProps) {
  const [configs, setConfigs] = useState<LlmConfig[] | null>(null);
  const [catalog, setCatalog] = useState<ModelCatalogGroup[] | null>(null);
  const [boundId, setBoundId] = useState<string | null>(agent.llm_config_id ?? null);
  const [bindingId, setBindingId] = useState<string | null>(null);
  const [bindingCatalogKey, setBindingCatalogKey] = useState<string | null>(null);
  /** 本次会话内绑定成功的平台模型（当前绑定卡片即时反映，不等运行时状态轮询） */
  const [platformBound, setPlatformBound] = useState<{ label: string; model: string } | null>(null);
  const [budgetText, setBudgetText] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<LlmFormState>(EMPTY_LLM_FORM);
  const [creating, setCreating] = useState(false);
  const savedHint = useSavedHint(runtimeRunning);

  // 事件处理中复用（创建配置 / 绑定平台模型后重载）
  const reload = useCallback(async () => {
    const [list, detail] = await Promise.all([listLlmConfigs(), getAgent(agent.id)]);
    setConfigs(list);
    const bid = (detail.llm_config_id as string | null) ?? agent.llm_config_id ?? null;
    setBoundId(bid);
    const cap = (detail.budget as Record<string, unknown> | undefined)?.monthly_cost_cap;
    setBudgetText(typeof cap === 'number' && cap > 0 ? String(cap) : '');
  }, [agent.id, agent.llm_config_id]);

  // 挂载时加载（setState 均在 .then/.catch 回调内）；目录加载失败不阻塞自建配置
  useEffect(() => {
    let cancelled = false;
    Promise.all([listLlmConfigs(), getAgent(agent.id)])
      .then(([list, detail]) => {
        if (cancelled) return;
        setConfigs(list);
        const bid = (detail.llm_config_id as string | null) ?? agent.llm_config_id ?? null;
        setBoundId(bid);
        const cap = (detail.budget as Record<string, unknown> | undefined)?.monthly_cost_cap;
        setBudgetText(typeof cap === 'number' && cap > 0 ? String(cap) : '');
      })
      .catch((err) => {
        if (cancelled) return;
        toast(describeError(err) || '模型配置加载失败', 'error');
        setConfigs([]);
      });
    fetchModelCatalog()
      .then((data) => {
        if (!cancelled) setCatalog(data.groups);
      })
      .catch(() => {
        if (!cancelled) setCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [agent.id, agent.llm_config_id]);

  const boundConfig = configs?.find((c) => c.id === boundId) ?? null;

  const handleBind = async (config: LlmConfig) => {
    setBindingId(config.id);
    try {
      await updateAgent(agent.id, { llm_config_id: config.id });
      setBoundId(config.id);
      setPlatformBound(null);
      savedHint(`已绑定「${config.name}」`);
    } catch (err) {
      toast(describeError(err) || '绑定失败', 'error');
    } finally {
      setBindingId(null);
    }
  };

  /** 绑定平台推荐模型：422=条目不可用 / 503=平台未配置 key（detail 中文直接 toast） */
  const handleBindPlatform = async (entry: ModelCatalogEntry) => {
    setBindingCatalogKey(entry.key);
    try {
      const res = await bindPlatformModel(agent.id, entry.key);
      setBoundId(res.llm_config_id);
      setPlatformBound({ label: entry.label, model: res.model });
      savedHint(`已绑定平台模型「${entry.label}」`);
      void reload();
    } catch (err) {
      toast(describeError(err) || '绑定平台模型失败', 'error');
    } finally {
      setBindingCatalogKey(null);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.model.trim() || !form.apiKey.trim()) {
      toast('请填写名称、模型与 API Key', 'error');
      return;
    }
    setCreating(true);
    try {
      await createLlmConfig({
        name: form.name.trim(),
        provider: form.provider,
        model: form.model.trim(),
        api_key: form.apiKey.trim(),
        base_url: form.baseUrl.trim() || undefined,
      });
      toast('模型配置已创建，可在列表中选择绑定', 'success');
      setFormOpen(false);
      setForm(EMPTY_LLM_FORM);
      void reload();
    } catch (err) {
      toast(describeError(err) || '创建失败', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveBudget = async () => {
    const trimmed = budgetText.trim();
    const cap = trimmed === '' ? null : Number(trimmed);
    if (trimmed !== '' && (Number.isNaN(cap) || (cap as number) < 0)) {
      toast('预算上限需为不小于 0 的数字', 'error');
      return;
    }
    setSavingBudget(true);
    try {
      await updateAgent(agent.id, { budget: { monthly_cost_cap: cap } });
      savedHint(trimmed === '' ? '已移除预算上限' : `月度预算上限已设为 ¥${cap}`);
      onBudgetChanged?.(agent.id, cap);
    } catch (err) {
      toast(describeError(err) || '保存失败', 'error');
    } finally {
      setSavingBudget(false);
    }
  };

  // 平台体验模型：运行时状态为 platform，或本次会话刚绑定平台目录模型时展示标识卡
  const onPlatformModel = !boundConfig && (modelSource === 'platform' || platformBound !== null);

  return (
    <div className="cfg-tab-content">
      {/* 当前绑定 */}
      <div className="cfg-card">
        <div className="cfg-card__title">当前绑定</div>
        {boundConfig ? (
          <div className="cfg-card__body">
            <div className="cfg-item__name">{boundConfig.name}</div>
            <div className="cfg-item__desc">
              {boundConfig.provider} · {boundConfig.model}
            </div>
          </div>
        ) : onPlatformModel ? (
          <div className="cfg-card__body cfg-platform-model">
            <div className="cfg-platform-model__head">
              <Sparkles size={15} />
              <span>{platformBound ? `平台模型 · ${platformBound.label}` : '当前使用平台体验模型'}</span>
            </div>
            <div className="cfg-platform-model__desc">
              {daily
                ? `今日平台额度：token ${daily.tokens.used}/${daily.tokens.grant} · 时长 ${daily.minutes.used}/${daily.minutes.grant} 分钟`
                : '平台每日提供有限的免费体验额度'}
              。配置自己的模型 Key 即可不限量。
            </div>
          </div>
        ) : (
          <div className="cfg-card__body cfg-muted">
            <TriangleAlert size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
            尚未绑定模型 —— Agent 需要绑定模型后才能启动对话。
          </div>
        )}
      </div>

      {/* 平台推荐（目录按 文本/视觉/图像/视频 分组；coming_soon 灰显禁用） */}
      <div className="cfg-field">
        <label className="form-label">平台推荐</label>
        {catalog === null ? (
          <div className="cfg-state">
            <Loader2 size={18} className="spin" />
            加载中…
          </div>
        ) : catalog.length === 0 ? (
          <div className="cfg-state">平台暂未开放推荐模型。</div>
        ) : (
          catalog.map((group) => (
            <div key={group.modality} className="cfg-catalog-group">
              <div className="cfg-catalog-group__label">{group.label}</div>
              <div className="cfg-list">
                {group.entries.map((entry) => {
                  const Icon = MODALITY_ICONS[entry.modality] ?? MessageSquareText;
                  const disabled = !entry.available;
                  return (
                    <div key={entry.key} className={`cfg-item cfg-catalog-item${disabled ? ' cfg-catalog-item--soon' : ''}`}>
                      <span className="cfg-catalog-item__icon">
                        <Icon size={15} />
                      </span>
                      <div className="cfg-item__main">
                        <div className="cfg-item__name">
                          {entry.label}
                          {disabled && <span className="badge">即将上线</span>}
                        </div>
                        {entry.description && <div className="cfg-item__desc">{entry.description}</div>}
                      </div>
                      {!disabled && (
                        <div className="cfg-item__actions">
                          <button
                            type="button"
                            className="btn cfg-btn-sm"
                            disabled={bindingCatalogKey !== null || bindingId !== null}
                            onClick={() => void handleBindPlatform(entry)}
                            aria-label={`使用平台模型「${entry.label}」`}
                          >
                            {bindingCatalogKey === entry.key ? <Loader2 size={12} className="spin" /> : '使用'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* 我的模型（仅当前用户自建配置） */}
      <div className="cfg-field">
        <label className="form-label">我的模型</label>
        {configs === null ? (
          <div className="cfg-state">
            <Loader2 size={18} className="spin" />
            加载中…
          </div>
        ) : configs.length === 0 ? (
          <div className="cfg-state">还没有自建模型配置，点下方「新建配置」添加，配置自己的 Key 即可不限量。</div>
        ) : (
          <div className="cfg-list">
            {configs.map((c) => (
              <div key={c.id} className={`cfg-item${boundId === c.id ? ' cfg-item--bound' : ''}`}>
                <div className="cfg-item__main">
                  <div className="cfg-item__name">
                    {c.name}
                    {boundId === c.id && <span className="badge badge--success">已绑定</span>}
                  </div>
                  <div className="cfg-item__desc">
                    {c.provider} · {c.model}
                    {c.base_url ? ` · ${c.base_url}` : ''}
                  </div>
                </div>
                {boundId !== c.id && (
                  <div className="cfg-item__actions">
                    <button
                      type="button"
                      className="btn cfg-btn-sm"
                      disabled={bindingId !== null || bindingCatalogKey !== null}
                      onClick={() => void handleBind(c)}
                      aria-label={`绑定模型「${c.name}」`}
                    >
                      {bindingId === c.id ? <Loader2 size={12} className="spin" /> : '绑定'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="cfg-save-bar">
        <button type="button" className="btn cfg-btn-sm" onClick={() => setFormOpen(true)}>
          <Plus size={13} />
          新建配置
        </button>
      </div>

      {/* 预算设置 */}
      <div className="cfg-card">
        <div className="cfg-card__title">月度预算上限</div>
        <div className="cfg-card__body">
          <div className="cfg-field__row">
            <input
              className="input"
              type="number"
              min={0}
              step={0.01}
              placeholder="如 50（元/月），留空表示不设限"
              value={budgetText}
              onChange={(e) => setBudgetText(e.target.value)}
              aria-label="月度预算上限"
            />
            <button type="button" className="btn btn-primary cfg-btn-sm" onClick={() => void handleSaveBudget()} disabled={savingBudget}>
              {savingBudget && <Loader2 size={12} className="spin" />}
              {savingBudget ? '保存中…' : '保存'}
            </button>
          </div>
          <div className="cfg-hint">当月花费超过上限后，Agent 将停止回复并提示调整预算。</div>
        </div>
      </div>

      {/* 新建配置对话框 */}
      <Modal
        open={formOpen}
        title="新建模型配置"
        width="md"
        onClose={() => setFormOpen(false)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setFormOpen(false)} disabled={creating}>
              取消
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleCreate()} disabled={creating}>
              {creating && <Loader2 size={14} className="spin" />}
              {creating ? '创建中…' : '创建'}
            </button>
          </>
        }
      >
        <div className="form-stack">
          <div className="cfg-field">
            <label className="form-label" htmlFor="llm-name">
              名称 <span style={{ color: 'var(--pg-danger)' }}>*</span>
            </label>
            <input id="llm-name" className="input" placeholder="比如：我的 DeepSeek" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="llm-provider">
              Provider
            </label>
            <select id="llm-provider" className="input" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
              {PROVIDER_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="llm-model">
              模型 <span style={{ color: 'var(--pg-danger)' }}>*</span>
            </label>
            <input id="llm-model" className="input" placeholder="比如：deepseek-chat" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="llm-apikey">
              API Key <span style={{ color: 'var(--pg-danger)' }}>*</span>
            </label>
            <input id="llm-apikey" className="input" type="password" placeholder="sk-…" value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })} autoComplete="off" />
            <div className="cfg-hint">加密存储，保存后不会再次回显。</div>
          </div>
          <div className="cfg-field">
            <label className="form-label" htmlFor="llm-baseurl">
              Base URL（可选）
            </label>
            <input id="llm-baseurl" className="input" placeholder="https://api.deepseek.com/v1" value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 * 面板主体
 * ════════════════════════════════════════════════════════════════ */

export interface ConfigPanelProps {
  agent: PlaygroundAgent | null;
  /** 平台每日额度（bootstrap usage.daily） */
  daily?: DailyQuota | null;
  /** 模型 Tab 保存预算成功后回调（顶栏进度条即时反映） */
  onBudgetChanged?: (agentId: string, cap: number | null) => void;
}

export default function ConfigPanel({ agent, daily, onBudgetChanged }: ConfigPanelProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('persona');
  const { status: runtimeStatus, info: runtimeInfo } = useAgentRuntimeStatus(agent?.id, { pollMs: 5000 });
  const runtimeRunning = isRuntimeRunning(runtimeStatus);
  const modelSource = runtimeInfo?.model_source ?? null;

  return (
    <>
      <div className="pg-panel-head">
        <span className="pg-panel-title">{agent ? `配置 · ${agent.name}` : '配置面板'}</span>
      </div>

      {agent ? (
        <>
          <div className="cfg-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                className={`cfg-tab${activeTab === t.key ? ' cfg-tab--active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="cfg-hint cfg-hint--top">{TAB_HINTS[activeTab]}</div>

          <div className="cfg-body" key={agent.id}>
            {activeTab === 'persona' && <PersonaTab agent={agent} runtimeRunning={runtimeRunning} />}
            {activeTab === 'skills' && <SkillsTab agent={agent} runtimeRunning={runtimeRunning} />}
            {activeTab === 'mcp' && <McpTab agent={agent} runtimeRunning={runtimeRunning} />}
            {activeTab === 'knowledge' && <KnowledgeTab agent={agent} />}
            {activeTab === 'llm' && (
              <LlmTab
                agent={agent}
                runtimeRunning={runtimeRunning}
                daily={daily}
                modelSource={modelSource}
                onBudgetChanged={onBudgetChanged}
              />
            )}
          </div>
        </>
      ) : (
        <div className="cfg-state">选择左侧 Agent 后，在这里配置它的角色、技能、工具、知识库与模型。</div>
      )}
    </>
  );
}
